import {
  // general helpers
  or,
  and,
  not,
  never,
  always, // eslint-disable-line no-unused-vars
  // window
  isViewCheckpointMode,
  // survey state
  isStateDraft,
  isStateUnderApproval,
  isStateUnderEdit,
  isStateRunning,
  isStateApproved,
  isStatePaused,
  isStatePausedCpi,
  isStatePausedUnderCompletion,
  isStatePausedUnderProbation,
  isStatePausedQuotasFillingUp,
  isStateInvoiced,
  isStateCompleted,
  isStateCancelled,
  isStateFailed,
  isStatePendingToComplete,
  // survey type
  isSurveyTypeRegular,
  isSurveyTypeInternal,
  isSurveyTypePeeq,
  isSurveyTypeDistributionLink,
  // isSurveyConfigWithFraudChecks,
  isSurveyTypeThirdParty,
  // survey helpers
  isSurveyRecurring,
  // user
  isUserAdmin,
  isUserResearcher,
  isUserGuest,
  isUserProdege,
  isUserPublisher,
  // perks
  hasPerkDisqualificationRules,
  hasPerkTranslations,
  hasPerkSurveyTheming,
  hasPerkThirdParty,
  hasPerkDistributionLink,
  hasPerkTemplates,
  hasPerkScheduling,
  hasPerkAdvancedSurveyTheming,
  hasPerkHiddenVariables,
  hasPerkLoopAndMerge,
  hasPerkEditLiveQuestionnaire,
  // survey config
  isSurveyConfigManagedTargeting,
  isSurveyConfigPricingRealTime,
  isSurveyConfigWithFraudChecks,
  isSurveyConfigNoPricing,
  isSurveyScheduled,
  // Questionnaire revisions undo/redo
  isQuestionnaireInRevisionPreviewMode,
  // ai survey builder
  isAiBuilderIdle,
  // hidden variables
  hasHiddenVariablesData,
  hasPerkConversational,
  // Live Edit Questionnaire
  isItemFromCurrentRevision,
  isConjointGroup,
  isConjointGroupFromCurrentRevision,
  hasQuestionnaireCarryForward,
  hasQuestionnaireLoopAndMergeFromQuestion,
  isItemInsideGroup,
  hasUnconditionalHideAnswerLogic,
  isAllowedUnconditionalHideAnswerLogicOnNode,
  isFinalisedUnderEdit,
  isRandomizedQuestionnaire,
  hasPerkQuotasSimple,
  hasPerkQuotasAdvanced,
  hasPerkMinimumResponses,
  itemHasCarryForwardFromPreviousRevision,
  isHiddenNodeByLogic,
  isScreeningNode,
  belongsToVanWestendorpGroup,
  isCustomTargetingNode,
  canShowDisplayLogicOnAnswers,
  canShowDisplayLogicOnRows,
  canShowDisplayLogicOnColumns,
  isItemInsideBasicGroupNode,
  groupHasQuestionFromPreviousRevision,
  canMoveNodeAfterNode,
  hasPermissionQuestionReorderMoveInsideGroup,
} from './helpers';

import {
  EDIT_PERMISSIONS,
  NAVIGATION_PERMISSIONS,
  VISIBILITY_PERMISSIONS,
} from './constants';

const LEGACY_SURVEY_TYPE_ACCESS = or(
  isUserAdmin,
  and(
    not(isSurveyTypeRegular),
    not(isSurveyTypeThirdParty),
    not(isSurveyTypeInternal),
  ),
);

const COMMON_PERMISSION_TEMPLATES = {
  DEFAULT_EDIT_PERMISSIONS: and(
    isAiBuilderIdle,
    or(
      isStateDraft,
      and(
        or(isSurveyTypeRegular, isSurveyTypeDistributionLink, isSurveyTypePeeq),
        isStateUnderApproval,
        isUserAdmin,
      ),
      and(
        or(isSurveyTypeRegular, isSurveyTypePeeq),
        isStateApproved,
        isUserResearcher,
      ),
      and(
        and(isSurveyTypePeeq, not(isSurveyConfigManagedTargeting)),
        isStateUnderEdit,
        hasPerkEditLiveQuestionnaire,
      ),
      and(
        isSurveyTypeDistributionLink,
        isStateUnderEdit,
        hasPerkEditLiveQuestionnaire,
      ),
    ),
    not(isQuestionnaireInRevisionPreviewMode),
    not(isViewCheckpointMode),
  ),
  DEFAULT_EDIT_PROMPT_PERMISSIONS: and(
    isAiBuilderIdle,
    not(isQuestionnaireInRevisionPreviewMode),
    not(isViewCheckpointMode),
    or(
      isStateDraft,
      isStateApproved,
      and(
        or(
          isSurveyTypeRegular,
          and(isSurveyTypePeeq, isSurveyConfigManagedTargeting),
        ),
        not(isStateUnderApproval),
        or(isUserResearcher, isUserAdmin),
      ),
      and(
        isSurveyTypePeeq,
        not(isSurveyConfigManagedTargeting),
        isStateUnderEdit,
      ),
      and(isSurveyTypeDistributionLink, isStateUnderEdit),
      and(
        or(isSurveyTypeRegular, isSurveyTypeDistributionLink, isSurveyTypePeeq),
        isStateUnderApproval,
        isUserAdmin,
      ),
      and(
        isSurveyTypeInternal,
        not(isStateUnderApproval),
        or(isUserPublisher, isUserAdmin),
      ),
      and(
        and(isSurveyTypePeeq, not(isSurveyConfigManagedTargeting)),
        isStateUnderEdit,
        hasPerkEditLiveQuestionnaire,
      ),
    ),
  ),
  PREVIEW_MODE_PERMISSION: and(
    not(isQuestionnaireInRevisionPreviewMode),
    not(isViewCheckpointMode),
  ),
  DEFAULT_EDIT_HIDDEN_VARIABLES: and(
    isAiBuilderIdle,
    not(isQuestionnaireInRevisionPreviewMode),
    not(isViewCheckpointMode),
    or(
      isStateDraft,
      and(isSurveyTypePeeq, isStateUnderApproval, isUserAdmin),
      and(isSurveyTypePeeq, isStateApproved, isUserResearcher),
    ),
  ),
  DEFAULT_EDIT_LIVE_QUESTIONNAIRE: or(
    and(isStateUnderEdit, isItemFromCurrentRevision),
    not(isStateUnderEdit),
  ),
};

const DEFAULT_EDIT_PERMISSIONS_ADL = or(
  COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  and(
    or(isSurveyTypeDistributionLink, isSurveyTypePeeq),
    isStateUnderEdit,
    or(isUserResearcher, isUserAdmin),
    not(isQuestionnaireInRevisionPreviewMode),
    isAiBuilderIdle,
    not(isViewCheckpointMode),
  ),
);

export const SURVEY_EDIT_PERMISSIONS = {
  // question related permissions
  [EDIT_PERMISSIONS.QUESTION_ADD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    or(
      not(isStateUnderEdit),
      and(isStateUnderEdit, isItemFromCurrentRevision),
      and(
        isStateUnderEdit,
        not(isScreeningNode),
        or(isItemInsideBasicGroupNode, not(isItemInsideGroup)),
      ),
    ),
  ),
  [EDIT_PERMISSIONS.QUESTION_DELETE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.QUESTION_REORDER_MOVE_AFTER]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    or(
      not(isStateUnderEdit),
      and(isStateUnderEdit, isItemFromCurrentRevision),
      and(
        not(isScreeningNode),
        or(
          isItemInsideBasicGroupNode,
          and(not(isItemInsideBasicGroupNode), not(isItemInsideGroup)),
        ),
        canMoveNodeAfterNode,
      ),
    ),
  ),
  [EDIT_PERMISSIONS.QUESTION_REORDER_MOVE_INSIDE_GROUP]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    or(
      not(isStateUnderEdit),
      and(isStateUnderEdit, isItemFromCurrentRevision),
      hasPermissionQuestionReorderMoveInsideGroup,
    ),
  ),
  [EDIT_PERMISSIONS.QUESTION_REORDER]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    or(
      not(isStateUnderEdit),
      and(isStateUnderEdit, isItemFromCurrentRevision),
      and(
        isStateUnderEdit,
        not(isScreeningNode),
        or(
          isItemInsideBasicGroupNode,
          and(not(isItemInsideBasicGroupNode), not(isItemInsideGroup)),
        ),
      ),
    ),
  ),
  [EDIT_PERMISSIONS.QUESTION_DUPLICATE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    or(
      not(isStateUnderEdit),
      and(isStateUnderEdit, isItemFromCurrentRevision),
      and(
        isStateUnderEdit,
        not(isScreeningNode),
        or(isItemInsideBasicGroupNode, not(isItemInsideGroup)),
      ),
    ),
  ),
  [EDIT_PERMISSIONS.QUESTION_EDIT_ADL]: DEFAULT_EDIT_PERMISSIONS_ADL,
  [EDIT_PERMISSIONS.QUESTION_EDIT_PROMPT]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
    not(isHiddenNodeByLogic),
  ),
  [EDIT_PERMISSIONS.QUESTION_CHANGE_TYPE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),

  // answer related permissions
  [EDIT_PERMISSIONS.ANSWER_ADD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    or(
      not(isStateUnderEdit),
      and(
        isStateUnderEdit,
        hasPerkEditLiveQuestionnaire,
        or(
          isItemFromCurrentRevision,
          and(not(isItemFromCurrentRevision), not(isItemInsideGroup)),
        ),
      ),
    ),
  ),
  [EDIT_PERMISSIONS.ANSWER_DELETE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.ANSWER_EDIT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.ANSWER_EDIT_PROMPT]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
    not(isHiddenNodeByLogic),
  ),

  [EDIT_PERMISSIONS.ANSWER_REORDER]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  ),
  [EDIT_PERMISSIONS.ANSWER_EDIT_MEDIA]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.ANSWER_EXCLUSIVE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.MATRIX_TOGGLE_REQUIRED_CELL]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.ANSWER_SCREENING_ALLOWED]: or(
    isStateDraft,
    isStateUnderEdit,
  ),
  [EDIT_PERMISSIONS.ANSWER_SCREENING_QUOTA]: or(
    or(isStateDraft, isStateUnderEdit, isStateApproved),
    and(isUserAdmin, isStateUnderApproval),
  ),
  [EDIT_PERMISSIONS.ANSWER_VISIBILITY]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    not(isRandomizedQuestionnaire),
  ),
  // setting related permissions
  [EDIT_PERMISSIONS.SETTING_RECALL]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_CARRY_FORWARD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    not(and(not(isStateDraft), itemHasCarryForwardFromPreviousRevision)),
  ),
  [EDIT_PERMISSIONS.SETTING_CARRY_FORWARD_CONFIG]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_CARRY_FORWARD_ADD_CONFIG]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_MEDIA]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_DECIMAL_LIMIT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_LIMITS]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_SIGN]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_RATING_STARS]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_RANKING]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_LIMIT_RANGE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_SLIDER_STEP]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_SLIDER_SHOW_VALUE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  ),
  [EDIT_PERMISSIONS.SETTING_SHUFFLE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_BATCH_ANSWERS]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_PREDEFINED_ANSWERS]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_NONE_OF_THE_ABOVE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
    or(
      not(isConjointGroup),
      and(isConjointGroup, not(isStateUnderEdit)),
      and(isConjointGroup, isConjointGroupFromCurrentRevision),
    ),
  ),
  [EDIT_PERMISSIONS.SETTING_NONE_OF_THESE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_OTHER]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_ADVANCED_LOGIC]: DEFAULT_EDIT_PERMISSIONS_ADL,
  [EDIT_PERMISSIONS.SETTING_DISPLAY_LOGIC]: DEFAULT_EDIT_PERMISSIONS_ADL,
  [EDIT_PERMISSIONS.SETTING_SHUFFLE_QUESTIONNAIRE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    or(
      not(isStateUnderEdit),
      and(
        isStateUnderEdit,
        not(hasQuestionnaireCarryForward),
        not(hasQuestionnaireLoopAndMergeFromQuestion),
      ),
    ),
  ),
  [EDIT_PERMISSIONS.SETTING_AB_TEST_TYPE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_AB_TEST_CONCEPT_NUMBER]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_AB_TEST_DEFAULT_QUESTIONS]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_AB_TEST_LIKELIHOOD_QUESTIONS]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_AB_TEST_ATTRIBUTES]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_CONJOINT_TYPE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_CONJOINT_MAX_SETS]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_CONJOINT_EXCLUDED_SETS]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_VAN_WESTENDORP_QUESTIONS_ORDER]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_VAN_WESTENDORP_ALLOW_DECIMAL]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_VAN_WESTENDORP_LIMIT_PRICE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_VAN_WESTENDORP_MNS_EXTENSION]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_MAXDIFF_MESSAGES]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_BIPOLAR_STYLE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_APPEARANCE_STYLE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_APPEARANCE_INPUT_SIZE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_LIMIT_TEXT_LENGTH]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_QUOTAS_SCREENING]: and(
    isAiBuilderIdle,
    or(
      or(isStateDraft, isStateUnderEdit, isStateApproved),
      and(isUserAdmin, isStateUnderApproval),
    ),
    not(isQuestionnaireInRevisionPreviewMode),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.SETTING_QUOTAS_SIMPLE]: and(
    hasPerkQuotasSimple,
    isAiBuilderIdle,
    or(
      or(isStateDraft, isStateUnderEdit, isStateApproved),
      and(isUserAdmin, isStateUnderApproval),
    ),
    not(isQuestionnaireInRevisionPreviewMode),
  ),
  [EDIT_PERMISSIONS.SETTING_MINIMUM_RESPONSES]: and(
    hasPerkMinimumResponses,
    isAiBuilderIdle,
    or(
      or(isStateDraft, isStateUnderEdit, isStateApproved),
      and(isUserAdmin, isStateUnderApproval),
    ),
    not(isQuestionnaireInRevisionPreviewMode),
  ),
  [EDIT_PERMISSIONS.SETTING_QUOTAS_ADVANCED]: and(
    hasPerkQuotasAdvanced,
    isAiBuilderIdle,
    or(
      or(isStateDraft, isStateUnderEdit, isStateApproved),
      and(isUserAdmin, isStateUnderApproval),
    ),
    not(isQuestionnaireInRevisionPreviewMode),
  ),
  [EDIT_PERMISSIONS.SETTING_DISPLAY_SAME_PAGE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_LOOP_AND_MERGE_WITH_CARRY_FORWARD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.SETTING_LOOP_AND_MERGE_RANDOMIZE_LOOP]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SETTING_LOOP_AND_MERGE_MAX_LOOPS]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.QUESTION_TOGGLE_EDIT_RANKING]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.QUESTION_EDIT_RANKING]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,

  // conversational question related permissions
  [EDIT_PERMISSIONS.CONVERSATIONAL_ANSWER_ADD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONVERSATIONAL_ANSWER_DELETE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONVERSATIONAL_ANSWER_EDIT]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONVERSATIONAL_ANSWER_REORDER]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),

  // group related permissions
  // THESE SEEMS UNUSED ANYONE HAS ANY IDEA WHY?
  // [EDIT_PERMISSIONS.GROUP_SHUFFLE]:
  //   COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  // [EDIT_PERMISSIONS.GROUP_QUESTIONS_SHUFFLE]:
  //   COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.GROUP_DELETE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.GROUP_ADD]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.GROUP_REORDER]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.GROUP_DELETE_ALL]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    not(groupHasQuestionFromPreviousRevision),
  ),
  // THESE SEEMS UNUSED ANYONE HAS ANY IDEA WHY?
  // [EDIT_PERMISSIONS.GROUP_QUESTIONS_MOVE]:
  //   COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,

  // AB related permissions
  [EDIT_PERMISSIONS.AB_ATTRIBUTE_ADD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.AB_ATTRIBUTE_DELETE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.AB_ATTRIBUTE_EDIT_PROMPT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  // THESE SEEMS UNUSED ANYONE HAS ANY IDEA WHY?
  // [EDIT_PERMISSIONS.AB_ATTRIBUTE_CONTENT_ADD]:
  //   COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  // [EDIT_PERMISSIONS.AB_ATTRIBUTE_CONTENT_DELETE]:
  //   COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.AB_ATTRIBUTE_CONTENT_EDIT_PROMPT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.AB_ATTRIBUTE_CONTENT_EDIT_MEDIA]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.AB_CONCEPT_ADD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.AB_CONCEPT_DELETE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.AB_CONCEPT_EDIT_PROMPT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,

  // Van Westendorp related permissions
  [EDIT_PERMISSIONS.VAN_WESTENDORP_ATTRIBUTE_CONTENT_ADD]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.VAN_WESTENDORP_ATTRIBUTE_CONTENT_DELETE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.VAN_WESTENDORP_ATTRIBUTE_CONTENT_EDIT_PROMPT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.VAN_WESTENDORP_ATTRIBUTE_CONTENT_EDIT_MEDIA]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.VAN_WESTENDORP_CONCEPT_EDIT_PROMPT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,

  // Loop and Merge related permissions
  [EDIT_PERMISSIONS.LOOP_AND_MERGE_ATTRIBUTE_ADD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.LOOP_AND_MERGE_ATTRIBUTE_DELETE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.LOOP_AND_MERGE_CONCEPT_ADD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.LOOP_AND_MERGE_CONCEPT_DELETE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.LOOP_AND_MERGE_ATTRIBUTE_EDIT_PROMPT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,

  // survey generic permissions
  [EDIT_PERMISSIONS.SURVEY_CHANGE_TERMS]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.SURVEY_CHANGE_LANGUAGE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.PREVIEW_MODE_PERMISSION]:
    COMMON_PERMISSION_TEMPLATES.PREVIEW_MODE_PERMISSION,
  [EDIT_PERMISSIONS.SURVEY_NAME_EDIT]: not(isViewCheckpointMode),

  [EDIT_PERMISSIONS.SURVEY_EDIT_DISQUALIFICATION_RULES]: and(
    isSurveyConfigWithFraudChecks,
    hasPerkDisqualificationRules,
    not(isViewCheckpointMode),
  ),
  // audience (distribution-link) permissions
  [EDIT_PERMISSIONS.AUDIENCE_TOGGLE_DEMOGRAPHIC]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
  [EDIT_PERMISSIONS.AUDIENCE_MODIFY_LANGUAGE]: and(
    or(isStateDraft, and(isUserAdmin, isStateUnderApproval)),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.DISTRIBUTION_LINK_CREATE]: and(
    hasPerkDistributionLink,
    or(isUserResearcher, isUserAdmin),
  ),

  // conjoint related permissions
  [EDIT_PERMISSIONS.CONJOINT_CONTENT_EDIT_PROMPT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_ADD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_REORDER]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_REMOVE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_EDIT_MEDIA]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_NAME]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_TYPE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_SORTABLE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_ADD]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_TEXT_EDIT]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_REORDER]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_REMOVE]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),
  [EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_EDIT_MEDIA]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
  ),

  [EDIT_PERMISSIONS.CREATE_THIRD_PARTY]: or(hasPerkThirdParty),
  [EDIT_PERMISSIONS.CREATE_CONVERSATIONAL]: and(
    hasPerkConversational,
    or(isSurveyTypeDistributionLink, isSurveyTypePeeq),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.CREATE_TEMPLATE]: or(
    isUserAdmin,
    and(isUserResearcher, hasPerkTemplates),
  ),
  [EDIT_PERMISSIONS.IMPORT_FROM_LIBRARY]: and(
    not(isUserGuest),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.DRILLDOWN_EDIT]: or(
    and(
      isStateDraft,
      COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
    ),
    and(
      isStateUnderEdit,
      COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
      COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
    ),
  ),
  [EDIT_PERMISSIONS.DRILLDOWN_DELETE]: or(
    and(
      isStateDraft,
      COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
    ),
    and(
      isStateUnderEdit,
      COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
      COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_LIVE_QUESTIONNAIRE,
    ),
  ),
  [EDIT_PERMISSIONS.THEME_EDIT]: and(
    hasPerkSurveyTheming,
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.THEME_PROGRESS_BAR_EDIT]: and(
    isSurveyTypeDistributionLink,
    or(isUserProdege, isUserAdmin),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.THEME_ADVANCED_SETTINGS]: and(
    hasPerkAdvancedSurveyTheming,
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.TRANSLATIONS]: and(
    or(hasPerkTranslations),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.EDIT_ADL]: and(
    or(isStateRunning, isStatePaused),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.EDIT_IR_LOI]: and(
    or(isStateDraft),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.PEEQ_AUDIENCE_STATE]: and(
    or(
      isStateDraft,
      isStateUnderEdit,
      and(isStateApproved, not(isSurveyConfigManagedTargeting)),
      and(isStateUnderApproval, isUserAdmin),
    ),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.PEEQ_CHANGE_LANGUAGE]: and(
    not(isStateUnderEdit),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.PEEQ_CHANGE_COUNTRY]: and(
    not(isStateUnderEdit),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.SAVE_REVISION]: and(
    or(
      and(isSurveyTypeRegular, or(isStateDraft, isStateUnderApproval)),
      and(isSurveyTypePeeq, or(isStateDraft, isStateUnderApproval)),
      and(
        and(isSurveyTypePeeq, not(isSurveyConfigManagedTargeting)),
        hasPerkEditLiveQuestionnaire,
        or(isStateDraft, isStateUnderApproval, isStateUnderEdit),
      ),
      and(isSurveyTypeDistributionLink, or(isStateDraft, isStateUnderEdit)),
    ),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.AI_BUILDER]: and(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PERMISSIONS,
    not(isStateUnderEdit),
    not(isViewCheckpointMode),
  ),
  [EDIT_PERMISSIONS.EXISTING_HIDDEN_VARIABLES]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_HIDDEN_VARIABLES,
  [EDIT_PERMISSIONS.HIDDEN_VARIABLES]: or(
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_HIDDEN_VARIABLES,
    and(
      or(isSurveyTypePeeq, isSurveyTypeDistributionLink),
      isStateUnderEdit,
      or(isUserResearcher, isUserAdmin),
      not(isQuestionnaireInRevisionPreviewMode),
      isAiBuilderIdle,
      not(isViewCheckpointMode),
    ),
  ),
  [EDIT_PERMISSIONS.UNCONDITIONAL_HIDE_ANSWER]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.UNCONDITIONAL_HIDE_ANSWER_RESTORE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
  [EDIT_PERMISSIONS.UNCONDITIONAL_HIDE_NODE]:
    COMMON_PERMISSION_TEMPLATES.DEFAULT_EDIT_PROMPT_PERMISSIONS,
};

export const SURVEY_NAVIGATION_PERMISSIONS = {
  [NAVIGATION_PERMISSIONS.SUMMARY]: isAiBuilderIdle,
  [NAVIGATION_PERMISSIONS.AUDIENCE]: and(
    LEGACY_SURVEY_TYPE_ACCESS,
    or(
      and(or(isStateDraft, isStateApproved), not(isSurveyTypeDistributionLink)),
      and(
        isStateUnderEdit,
        not(or(isSurveyTypeDistributionLink, isSurveyTypePeeq)),
      ),
      and(
        isStateUnderEdit,
        isSurveyTypePeeq,
        not(isSurveyConfigManagedTargeting),
      ),
      and(isSurveyTypeDistributionLink, or(isStateDraft, isStateUnderEdit)),
      isUserAdmin,
    ),
    isAiBuilderIdle,
  ),
  [NAVIGATION_PERMISSIONS.QUESTIONNAIRE]: and(
    LEGACY_SURVEY_TYPE_ACCESS,
    or(
      isStateDraft,
      isStateApproved,
      and(isStateRunning, isSurveyConfigManagedTargeting),
      and(isStatePaused, isSurveyConfigManagedTargeting),
      isStatePausedCpi,
      isStatePausedUnderCompletion,
      isStatePausedUnderProbation,
      isStateInvoiced,
      isStateUnderEdit,
      and(isStateFailed, isSurveyConfigManagedTargeting),
      and(isStateCompleted, isSurveyConfigManagedTargeting),
      and(isStateCancelled, isSurveyConfigManagedTargeting),
      isUserAdmin,
      and(isStatePausedQuotasFillingUp, isSurveyTypeDistributionLink),
    ),
  ),
  [NAVIGATION_PERMISSIONS.SURVEY_SETTINGS]: and(
    LEGACY_SURVEY_TYPE_ACCESS,
    or(
      and(
        isSurveyTypeDistributionLink,
        or(isStateDraft, isStateUnderEdit, isUserAdmin),
      ),
      isUserAdmin,
    ),
    isAiBuilderIdle,
  ),
  [NAVIGATION_PERMISSIONS.THIRD_PARTY_SETTINGS]: isUserAdmin,
  [NAVIGATION_PERMISSIONS.CHECKOUT]: and(
    LEGACY_SURVEY_TYPE_ACCESS,
    or(
      and(
        not(isSurveyConfigNoPricing),
        or(isStateDraft, isStateUnderEdit, isStateApproved),
      ),
      and(
        not(isSurveyConfigNoPricing),
        and(
          or(isStateRunning, isStateCompleted),
          hasPerkScheduling,
          isSurveyRecurring,
        ),
      ),
      isUserAdmin,
    ),
    isAiBuilderIdle,
    not(isViewCheckpointMode),
  ),
  [NAVIGATION_PERMISSIONS.DISQUALIFICATION_RULES_TAB]: and(
    hasPerkDisqualificationRules,
    isSurveyConfigWithFraudChecks,
    not(isSurveyConfigManagedTargeting),
    isAiBuilderIdle,
  ),
  [NAVIGATION_PERMISSIONS.LINK_SHARE]: and(
    or(isStateDraft, isStateUnderEdit, isUserAdmin),
    isAiBuilderIdle,
  ),
  [NAVIGATION_PERMISSIONS.TRANSLATIONS]: and(
    or(
      and(
        hasPerkTranslations,
        or(
          isStateRunning,
          isStatePaused,
          isStatePausedCpi,
          isStatePausedUnderCompletion,
          isStatePausedUnderProbation,
          isStateInvoiced,
          isStateUnderEdit,
          isStateCompleted,
          isStateCancelled,
          isStateFailed,
          isStatePendingToComplete,
        ),
      ),
      isUserAdmin,
    ),
    isAiBuilderIdle,
  ),
  [NAVIGATION_PERMISSIONS.RESULTS]: and(
    or(
      isStateRunning,
      isStatePaused,
      isStatePausedCpi,
      isStatePausedUnderCompletion,
      isStatePausedUnderProbation,
      isStatePausedQuotasFillingUp,
      isStateInvoiced,
      isStateUnderEdit,
      isStateCompleted,
      isStateCancelled,
      isStateFailed,
      isStatePendingToComplete,
      isUserAdmin,
    ),
    isAiBuilderIdle,
  ),
  [NAVIGATION_PERMISSIONS.HIDDEN_VARIABLES_TAB]: or(
    and(
      or(hasPerkHiddenVariables, hasHiddenVariablesData),
      or(isSurveyTypePeeq, isSurveyTypeDistributionLink),
    ),
    and(isUserAdmin, or(isSurveyTypePeeq, isSurveyTypeDistributionLink)),
  ),
  [NAVIGATION_PERMISSIONS.DISPLAY_LOGIC_TAB]: or(
    isSurveyTypePeeq,
    isSurveyTypeDistributionLink,
  ),
  [NAVIGATION_PERMISSIONS.ADVANCED_QUOTA_LOGIC_TAB]: and(
    or(isSurveyTypePeeq, isSurveyTypeDistributionLink),
    hasPerkQuotasAdvanced,
  ),
  [NAVIGATION_PERMISSIONS.SURVEY_THEMING_TAB]: not(isSurveyTypePeeq),
  [NAVIGATION_PERMISSIONS.ADVANCED_LOGIC_TAB]: always,
};

export const SURVEY_VISIBILITY_PERMISSIONS = {
  [VISIBILITY_PERMISSIONS.CHECKOUT_BUTTON]: and(
    LEGACY_SURVEY_TYPE_ACCESS,
    or(
      and(
        not(isSurveyConfigNoPricing),
        not(isSurveyTypePeeq),
        not(isSurveyTypeDistributionLink),
        or(isStateDraft, isStateUnderEdit),
      ),
      and(
        isSurveyTypePeeq,
        not(isSurveyConfigManagedTargeting),
        isStateApproved,
        not(isSurveyScheduled),
        not(isSurveyRecurring),
      ),
      and(isSurveyTypePeeq, isStateDraft),
    ),
    not(isViewCheckpointMode),
  ),
  [VISIBILITY_PERMISSIONS.COST_DETAILS]: and(
    or(isStateDraft, isStateUnderEdit),
    not(isSurveyConfigNoPricing),
    not(and(isStateUnderEdit, isSurveyTypePeeq, isSurveyConfigPricingRealTime)),
    not(isViewCheckpointMode),
  ),
  [VISIBILITY_PERMISSIONS.FEASIBILITY_DETAILS]: and(
    or(isStateDraft, isStateUnderEdit),
    not(isSurveyConfigNoPricing),
    not(and(isStateUnderEdit, isSurveyTypePeeq, isSurveyConfigPricingRealTime)),
    not(isViewCheckpointMode),
  ),
  [VISIBILITY_PERMISSIONS.TRANSLATIONS]: not(isSurveyTypeThirdParty),
  [VISIBILITY_PERMISSIONS.SERVICES]: not(isUserPublisher),
  [VISIBILITY_PERMISSIONS.INSIGHTS]: never,
  [VISIBILITY_PERMISSIONS.SHOW_QUESTIONNAIRE_REVISIONS]: or(
    and(
      isSurveyTypeRegular,
      or(isStateDraft, isStateApproved, isStateUnderApproval),
    ),
    and(
      isSurveyTypePeeq,
      or(isStateDraft, isStateApproved, isStateUnderApproval),
    ),
    and(
      isSurveyTypePeeq,
      hasPerkEditLiveQuestionnaire,
      or(isStateDraft, isStateApproved, isStateUnderApproval, isStateUnderEdit),
    ),
    and(
      isSurveyTypeDistributionLink,
      or(isStateDraft, isStateApproved, isStateUnderEdit),
    ),
  ),
  [VISIBILITY_PERMISSIONS.SHOW_EDIT_ADL]: and(
    isSurveyTypePeeq,
    isSurveyConfigManagedTargeting,
    or(isStateRunning, isStatePaused, isStateUnderEdit),
  ),
  [VISIBILITY_PERMISSIONS.CONVERSATIONAL_AI]: or(
    isSurveyTypePeeq,
    isSurveyTypeDistributionLink,
  ),
  [VISIBILITY_PERMISSIONS.LOOP_AND_MERGE]: and(hasPerkLoopAndMerge),
  [VISIBILITY_PERMISSIONS.AI_BUILDER]: and(
    not(isStateUnderEdit),
    or(isSurveyTypePeeq, isSurveyTypeDistributionLink),
  ),
  [VISIBILITY_PERMISSIONS.SHOW_UNCONDITIONAL_HIDE_ANSWER_BUTTON]: and(
    or(
      isSurveyTypeDistributionLink,
      and(isSurveyTypePeeq, not(isSurveyConfigManagedTargeting)),
    ),
    not(isStateDraft),
    not(and(isStateApproved, not(isFinalisedUnderEdit))),
    not(and(isStateUnderApproval, not(isFinalisedUnderEdit))),
    hasPerkEditLiveQuestionnaire,
    isAllowedUnconditionalHideAnswerLogicOnNode,
    not(isItemFromCurrentRevision),
    not(hasUnconditionalHideAnswerLogic),
  ),
  [VISIBILITY_PERMISSIONS.SHOW_UNCONDITIONAL_HIDE_ANSWER_RESTORE_BUTTON]: and(
    or(
      isSurveyTypeDistributionLink,
      and(isSurveyTypePeeq, not(isSurveyConfigManagedTargeting)),
    ),
    not(isStateDraft),
    not(and(isStateApproved, not(isFinalisedUnderEdit))),
    not(and(isStateUnderApproval, not(isFinalisedUnderEdit))),
    hasPerkEditLiveQuestionnaire,
    not(isItemFromCurrentRevision),
    hasUnconditionalHideAnswerLogic,
  ),
  [VISIBILITY_PERMISSIONS.SHOW_HIDE_NODE_BUTTON]: and(
    or(
      isSurveyTypeDistributionLink,
      and(isSurveyTypePeeq, not(isSurveyConfigManagedTargeting)),
    ),
    not(isStateDraft),
    not(isStateRunning),
    not(and(isStateApproved, not(isFinalisedUnderEdit))),
    not(and(isStateUnderApproval, not(isFinalisedUnderEdit))),
    hasPerkEditLiveQuestionnaire,
    not(isItemFromCurrentRevision),
    not(isScreeningNode),
    not(belongsToVanWestendorpGroup),
  ),
  [VISIBILITY_PERMISSIONS.SHOW_NODE_DISPLAY_LOGIC_BUTTON]: and(
    not(isScreeningNode),
    not(isCustomTargetingNode),
    not(belongsToVanWestendorpGroup),
  ),
  [VISIBILITY_PERMISSIONS.SHOW_ANSWERS_DISPLAY_LOGIC_BUTTON]: and(
    not(isCustomTargetingNode),
    canShowDisplayLogicOnAnswers,
  ),
  [VISIBILITY_PERMISSIONS.SHOW_ROWS_DISPLAY_LOGIC_BUTTON]: and(
    not(isCustomTargetingNode),
    canShowDisplayLogicOnRows,
  ),
  [VISIBILITY_PERMISSIONS.SHOW_COLUMNS_DISPLAY_LOGIC_BUTTON]: and(
    not(isCustomTargetingNode),
    canShowDisplayLogicOnColumns,
  ),
  [VISIBILITY_PERMISSIONS.SETTING_QUOTAS_SIMPLE]: and(hasPerkQuotasSimple),
  [VISIBILITY_PERMISSIONS.SETTING_MINIMUM_RESPONSES]: and(
    hasPerkMinimumResponses,
  ),
  [VISIBILITY_PERMISSIONS.SETTING_QUOTAS_ADVANCED]: and(hasPerkQuotasAdvanced),
  [VISIBILITY_PERMISSIONS.SHOW_EDIT_SURVEY_BUTTON]: and(
    not(isUserGuest),
    isSurveyTypeDistributionLink,
    not(isViewCheckpointMode),
    not(isQuestionnaireInRevisionPreviewMode),
    isAiBuilderIdle,
    or(
      isStateRunning,
      isStatePaused,
      isStateCancelled,
      isStateCompleted,
      isStatePausedQuotasFillingUp,
    ),
  ),
};
